import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantLogo, useTenantColors } from '../../contexts/TenantBrandingContext';
import { ComplianceAttestationModal } from '../Compliance/ComplianceAttestationModal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { api } from '../../lib/api';
import apiClient from '../../lib/api';
import { useFreeTrialFeature } from '../../contexts/FeatureFlagContext';

// Icons (you can replace these with your preferred icon library)
const MenuIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const XIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const LogoutIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  current?: boolean;
  description?: string;
}

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { user, tenant, tenants, logout, needsAttestation, completeAttestation, switchTenant } = useAuth();
  const { logoUrl, logoAlt } = useTenantLogo();
  const { primary, text } = useTenantColors();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(true); // Default to true to show mobile on first render
  const [isClient, setIsClient] = useState(false);
  const navigate = useNavigate();
  
  // Check billing status for trial expiration warnings
  const { data: billingInfo } = useQuery({
    queryKey: ['billing-info'],
    queryFn: async () => {
      try {
        const response = await api.billing.getInfo();
        return response.data;
      } catch (error) {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });
  
  // Check if trial has expired
  const trialExpired = billingInfo?.trial_end && new Date(billingInfo.trial_end) <= new Date();
  const isInactive = billingInfo?.status === 'inactive';
  const isIncomplete = billingInfo?.status === 'incomplete';
  const isTrialing = billingInfo?.status === 'trialing';
  
  // Check if card is required for trial - fetch from system features endpoint
  const { data: systemFeatures } = useQuery({
    queryKey: ['system-features'],
    queryFn: async () => {
      try {
        // Use apiClient to ensure correct base URL and headers
        const response = await apiClient.get('/api/v1/system/features');
        return response.data;
      } catch (error) {
        console.error('Failed to fetch system features:', error);
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const requireCardForTrial = systemFeatures?.require_card_for_trial || false;

  // Fetch feature availability to conditionally show navigation items
  const { data: features, isLoading: featuresLoading, error: featuresError } = useQuery({
    queryKey: ['features', tenant?.id], // Include tenant ID to refetch when tenant changes
    queryFn: async () => {
      const response = await api.billing.getFeatures();
      // Response structure: { data: { plan: string, plan_level: number, features: {...} } }
      const featuresData = response.data?.features || {};
      console.debug('Features loaded:', featuresData, 'Full response:', response.data);
      return featuresData;
    },
    enabled: !!user && !!tenant, // Only fetch if user and tenant exist
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });

  // Log feature errors and loading state for debugging
  useEffect(() => {
    if (featuresError) {
      console.error('Error loading features:', featuresError);
    }
    if (featuresLoading) {
      console.debug('Loading features...');
    }
    if (features) {
      console.debug('Features state:', features, 'white_label_branding:', features.white_label_branding);
    }
  }, [featuresError, featuresLoading, features]);

  // Check if we're on the client side (not SSR)
  useEffect(() => {
    setIsClient(true);
  }, []);
  
  // Redirect to checkout if billing is INCOMPLETE and card is required for trial
  useEffect(() => {
    // Only check if user is logged in
    if (!user || !tenant) return;
    
    // Don't redirect if we're already redirecting to checkout (prevents race conditions)
    if (sessionStorage.getItem('redirecting_to_checkout') === 'true') {
      console.log('MainLayout: Already redirecting to checkout, skipping redirect logic');
      return;
    }
    
    // Allow redirect even if on billing page if coming from attestation
    const redirectAfterAttestation = localStorage.getItem('redirect_to_checkout_after_attestation');
    if (location.pathname.startsWith('/billing') && !redirectAfterAttestation) return;
    
    // Wait for both billing info and system features to load
    if (!billingInfo || systemFeatures === undefined) return;
    
    // Check if card is required (use system features if available, otherwise assume false)
    const cardRequired = systemFeatures?.require_card_for_trial || false;
    if (!cardRequired) {
      // Clear redirect flag if card is not required
      sessionStorage.removeItem('incomplete_billing_redirect_attempted');
      return;
    }
    
    // Check if billing is missing (setup_required flag) or incomplete
    const isBillingMissing = billingInfo && (billingInfo as any).setup_required === true;
    const isIncomplete = billingInfo?.status === 'incomplete';
    
    // If billing is missing or INCOMPLETE and card is required for trial, redirect to checkout
    if (isBillingMissing || isIncomplete) {
      // Check if we should redirect (only once per session, avoid redirect loops)
      const shouldRedirect = sessionStorage.getItem('incomplete_billing_redirect_attempted') !== 'true';
      if (shouldRedirect) {
        sessionStorage.setItem('incomplete_billing_redirect_attempted', 'true');
        
        // Get plan - use billing info plan, or recover from localStorage, or default to starter
        let planToUse = billingInfo?.plan || 'starter';
        if (isBillingMissing) {
          // Try to recover plan from localStorage
          try {
            const pendingRegistration = localStorage.getItem('pending_registration');
            if (pendingRegistration) {
              const registrationData = JSON.parse(pendingRegistration);
              if (registrationData.plan) {
                planToUse = registrationData.plan;
                console.log(`MainLayout: Recovered plan '${planToUse}' from localStorage`);
              }
            }
          } catch (e) {
            console.warn('MainLayout: Failed to recover plan from localStorage:', e);
          }
        }
        
        // Create checkout session and redirect
        api.billing.createCheckoutSession(
          planToUse,
          `${window.location.origin}/billing/checkout-success?session_id={CHECKOUT_SESSION_ID}`,
          `${window.location.origin}${location.pathname}`
        ).then((response) => {
          window.location.href = response.data.checkout_url;
        }).catch((error) => {
          console.error('Failed to create checkout session:', error);
          // Clear the flag so user can try again
          sessionStorage.removeItem('incomplete_billing_redirect_attempted');
          // Navigate to billing page - it will handle the redirect
          navigate('/billing');
        });
      }
    } else {
      // Clear redirect flag if billing is no longer incomplete
      sessionStorage.removeItem('incomplete_billing_redirect_attempted');
    }
  }, [user, tenant, billingInfo, systemFeatures, location.pathname, navigate]);
  
  // Check if screen is mobile size - use matchMedia for better reliability
  useEffect(() => {
    if (!isClient) return;
    
    const checkIsMobile = () => {
      // Use both window width and media query for better reliability
      const width = window.innerWidth || document.documentElement.clientWidth || screen.width;
      const isMobileWidth = width < 1024;
      
      // Also check media query for better mobile detection
      const mediaQuery = window.matchMedia ? window.matchMedia('(max-width: 1023px)') : null;
      const isMobileMatch = mediaQuery ? mediaQuery.matches : isMobileWidth;
      
      setIsMobile(isMobileWidth || isMobileMatch);
    };
    
    // Check immediately
    checkIsMobile();
    
    // Listen for resize events with debounce
    let resizeTimeout: ReturnType<typeof setTimeout> | undefined;
    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(checkIsMobile, 100);
    };
    
    // Use matchMedia listener for better performance if available
    let mediaQuery: MediaQueryList | null = null;
    if (window.matchMedia) {
      mediaQuery = window.matchMedia('(max-width: 1023px)');
      mediaQuery.addEventListener('change', checkIsMobile);
    }
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', checkIsMobile);
    
    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', checkIsMobile);
      if (mediaQuery) {
        mediaQuery.removeEventListener('change', checkIsMobile);
      }
    };
  }, [isClient]);

  // Build navigation items with useMemo to recompute when features change
  const navigation: NavigationItem[] = useMemo(() => {
    // Base navigation items (always visible)
    const baseNavigation: NavigationItem[] = [
      { name: 'Dashboard', href: '/', icon: DashboardIcon, description: 'Overview of your reputation management performance' },
      { name: 'Campaigns', href: '/campaigns', icon: CampaignsIcon, description: 'Create and manage email and SMS campaigns' },
      { name: 'Feedback', href: '/feedback', icon: FeedbackIcon, description: 'Manage customer feedback with SLA tracking' },
      { name: 'Analytics', href: '/analytics', icon: AnalyticsIcon, description: 'View detailed metrics and performance reports' },
      { name: 'Templates', href: '/templates', icon: TemplatesIcon, description: 'Create and manage email and SMS templates' },
      { name: 'Sequences', href: '/sequences', icon: SequencesIcon, description: 'Create and manage multi-step message sequences' },
      { name: 'Contacts', href: '/contacts', icon: ContactsIcon, description: 'Manage your contact database and segments' },
      { name: 'Rating Gate', href: '/rating', icon: RatingIcon, description: 'Configure rating collection and review routing' },
      { name: 'Team', href: '/team', icon: UsersIcon, description: 'Manage team members and permissions' },
      { name: 'Branding', href: '/branding', icon: BrandingIcon, description: 'Customize your branding and configure white-label settings' },
    ];

    // Combine navigation items
    return [
      ...baseNavigation,
      { name: 'User Guide', href: '/user-guide', icon: BookOpenIcon, description: 'Step-by-step guide to get started with FeedbackGate' },
      { name: 'Settings', href: '/settings', icon: SettingsIcon, description: 'Configure account and application settings' },
    ];
  }, []);

  // Server admin navigation (only for superusers)
  const serverAdminNavigation: NavigationItem[] = user?.role === 'superuser' ? [
    { name: 'Server Settings', href: '/server/settings', icon: ServerSettingsIcon },
    { name: 'Server Admin', href: '/server/admin', icon: ServerAdminIcon },
  ] : [];

  // Update current page for navigation
  const updatedNavigation = navigation.map(item => ({
    ...item,
    current: location.pathname === item.href || 
             (item.href !== '/' && location.pathname.startsWith(item.href)),
  }));

  const updatedServerAdminNavigation = serverAdminNavigation.map(item => ({
    ...item,
    current: location.pathname === item.href,
  }));

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full overflow-x-hidden">
      {/* Compliance Attestation Modal */}
      <ComplianceAttestationModal
        open={needsAttestation}
        onComplete={completeAttestation}
      />
      
      {/* Mobile sidebar overlay - shown when menu is open */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white h-full overflow-hidden">
          <div className="absolute top-0 right-0 -mr-12 pt-2 z-50">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XIcon />
            </button>
          </div>
          <SidebarContent 
            navigation={updatedNavigation}
            serverAdminNavigation={updatedServerAdminNavigation}
            user={user}
            tenant={tenant}
            tenants={tenants}
            logoUrl={logoUrl}
            logoAlt={logoAlt}
            primary={primary}
            text={text}
            onLogout={handleLogout}
            onSwitchTenant={switchTenant}
          />
        </div>
      </div>

      {/* Mobile layout - show by default, hide when client loads and screen is large */}
      <div 
        className={`mobile-layout ${isClient && !isMobile ? 'hidden' : ''}`}
        style={{ 
          width: '100%',
          minHeight: '100vh',
          overflowX: 'hidden',
          backgroundColor: '#f9fafb',
          position: isClient && !isMobile ? 'absolute' : 'relative',
          zIndex: isClient && !isMobile ? -1 : 10,
          display: isClient && !isMobile ? 'none' : 'flex',
          flexDirection: 'column',
          visibility: isClient && !isMobile ? 'hidden' : 'visible'
        }}
      >
        {/* Top bar - mobile menu button */}
        <header style={{
          position: 'relative', // Changed from sticky to relative
          top: 0,
          zIndex: 20,
          paddingLeft: '0.25rem',
          paddingTop: '0.25rem',
          paddingBottom: '0.25rem',
          backgroundColor: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
          width: '100%',
          display: 'block',
          flexShrink: 0 // Prevent header from shrinking in flex layout
        }}>
          <button
            style={{
              marginLeft: '-0.125rem',
              marginTop: '-0.125rem',
              height: '3rem',
              width: '3rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '0.375rem',
              color: '#6b7280',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer'
            }}
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon />
          </button>
        </header>

        {/* Mobile main content area */}
        <main style={{
          backgroundColor: '#f9fafb',
          width: '100%',
          minHeight: '500px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          paddingTop: '1rem',
          paddingBottom: '1rem',
          overflowX: 'hidden',
          overflowY: 'auto',
          flex: '1 1 auto',
          visibility: 'visible',
          height: 'auto'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '100%',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            paddingTop: '0',
            paddingBottom: '2rem',
            overflowX: 'hidden',
            position: 'relative',
            display: 'block',
            visibility: 'visible',
            minHeight: '200px'
          }}>
            {/* Trial Expiration Warning Banner */}
            {billingInfo && (trialExpired || isInactive || isIncomplete) && (
              <div className="mt-4 mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-md overflow-x-hidden">
                <div className="flex flex-col sm:flex-row">
                  <div className="flex-shrink-0 mb-2 sm:mb-0">
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-0 sm:ml-3 flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-red-800">
                      {isIncomplete 
                        ? 'Payment Method Required' 
                        : trialExpired 
                          ? 'Your Free Trial Has Expired' 
                          : 'Account Access Restricted'}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>
                        {isIncomplete
                          ? 'Please add a payment method to start your free trial and access all features.'
                          : trialExpired 
                            ? 'Your free trial has ended. Please upgrade to a paid plan to continue using all features.'
                            : 'Your account is inactive. Please add a payment method to restore access.'}
                      </p>
                      <div className="mt-3">
                        <button
                          onClick={() => navigate('/billing')}
                          className="w-full sm:w-auto bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          {isIncomplete ? 'Add Payment Method' : 'Go to Billing & Upgrade'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Trial Expiring Soon Warning (3 days or less) */}
            {billingInfo && isTrialing && billingInfo.trial_end && !trialExpired && (
              (() => {
                const daysLeft = Math.ceil((new Date(billingInfo.trial_end).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                if (daysLeft <= 3 && daysLeft > 0) {
                  return (
                    <div className="mt-4 mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-md overflow-x-hidden">
                      <div className="flex flex-col sm:flex-row">
                        <div className="flex-shrink-0 mb-2 sm:mb-0">
                          <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-0 sm:ml-3 flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-yellow-800">
                            Your Free Trial Ends in {daysLeft} Day{daysLeft !== 1 ? 's' : ''}
                          </h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p>Upgrade now to avoid any interruption to your service.</p>
                            <div className="mt-3">
                              <button
                                onClick={() => navigate('/billing')}
                                className="w-full sm:w-auto bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                              >
                                Upgrade Now
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()
            )}
            
            {/* Page content */}
            <div 
              className="mobile-page-content" 
              style={{ 
                width: '100%', 
                maxWidth: '100%', 
                minHeight: '400px',
                position: 'relative',
                display: 'block',
                padding: 0,
                margin: 0,
                backgroundColor: '#f9fafb',
                color: '#1f2937',
                visibility: 'visible',
                opacity: 1
              }}
            >
              {/* Render children */}
              <div style={{ 
                width: '100%',
                display: 'block',
                position: 'relative',
                visibility: 'visible',
                opacity: 1,
                color: '#1f2937',
                backgroundColor: 'transparent'
              }}>
                {children || (
                  <div style={{
                    padding: '2rem',
                    backgroundColor: '#fef3c7',
                    border: '3px solid #f59e0b',
                    borderRadius: '0.5rem',
                    textAlign: 'center'
                  }}>
                    <p style={{ color: '#92400e', fontWeight: 'bold', fontSize: '1rem', margin: 0 }}>
                      ⚠️ No children content received. This indicates a rendering issue.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Desktop layout - only show when client loads and screen is large */}
      <div 
        className={`desktop-layout ${!isClient || isMobile ? 'hidden' : ''}`}
        style={{
          display: !isClient || isMobile ? 'none' : 'flex',
          height: '100vh',
          position: !isClient || isMobile ? 'absolute' : 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: !isClient || isMobile ? -1 : 1,
          visibility: !isClient || isMobile ? 'hidden' : 'visible'
        }}
      >
        {/* Desktop sidebar */}
        <div className="flex-shrink-0 w-64 h-full overflow-hidden">
          <SidebarContent 
            navigation={updatedNavigation}
            serverAdminNavigation={updatedServerAdminNavigation}
            user={user}
            tenant={tenant}
            tenants={tenants}
            logoUrl={logoUrl}
            logoAlt={logoAlt}
            primary={primary}
            text={text}
            onLogout={handleLogout}
            onSwitchTenant={switchTenant}
          />
        </div>

        {/* Main content */}
        <div className="flex-1 h-full overflow-y-auto overflow-x-hidden">
          {/* Page content */}
          <main className="h-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              {/* Trial Expiration Warning Banner */}
              {billingInfo && (trialExpired || isInactive) && (
                <div className="mt-4 mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-red-800">
                        {trialExpired ? 'Your Free Trial Has Expired' : 'Account Access Restricted'}
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>
                          {trialExpired 
                            ? 'Your free trial has ended. Please upgrade to a paid plan to continue using all features.'
                            : 'Your account is inactive. Please add a payment method to restore access.'}
                        </p>
                        <div className="mt-3">
                          <button
                            onClick={() => navigate('/billing')}
                            className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            Go to Billing & Upgrade
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Trial Expiring Soon Warning (3 days or less) */}
              {billingInfo && isTrialing && billingInfo.trial_end && !trialExpired && (
                (() => {
                  const daysLeft = Math.ceil((new Date(billingInfo.trial_end).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                  if (daysLeft <= 3 && daysLeft > 0) {
                    return (
                      <div className="mt-4 mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-md">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3 flex-1">
                            <h3 className="text-sm font-medium text-yellow-800">
                              Your Free Trial Ends in {daysLeft} Day{daysLeft !== 1 ? 's' : ''}
                            </h3>
                            <div className="mt-2 text-sm text-yellow-700">
                              <p>Upgrade now to avoid any interruption to your service.</p>
                              <div className="mt-3">
                                <button
                                  onClick={() => navigate('/billing')}
                                  className="bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                >
                                  Upgrade Now
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()
              )}
              
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

// Sidebar content component
interface SidebarContentProps {
  navigation: NavigationItem[];
  serverAdminNavigation: NavigationItem[];
  user: any;
  tenant: any;
  tenants: any[];
  logoUrl?: string;
  logoAlt: string;
  primary: string;
  text: string;
  onLogout: () => void;
  onSwitchTenant: (tenantId: string) => Promise<void>;
}

const SidebarContent: React.FC<SidebarContentProps> = ({
  navigation,
  serverAdminNavigation,
  user,
  tenant,
  tenants,
  logoUrl,
  logoAlt,
  primary,
  text,
  onLogout,
  onSwitchTenant,
}) => {
  const [showTenantSwitcher, setShowTenantSwitcher] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const handleTenantSwitch = async (tenantId: string) => {
    if (tenantId === tenant?.id) {
      setShowTenantSwitcher(false);
      return;
    }
    
    try {
      setIsSwitching(true);
      await onSwitchTenant(tenantId);
      setShowTenantSwitcher(false);
      // Reload page to refresh all tenant-specific data
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      alert('Failed to switch tenant. Please try again.');
    } finally {
      setIsSwitching(false);
    }
  };
  return (
    <div className="flex flex-col h-full border-r border-gray-200 bg-white">
      {/* Logo - fixed at top */}
      <div className="flex-shrink-0 pt-5 pb-4">
        <div className="flex items-center px-4">
          {logoUrl ? (
            <img className="h-8 w-auto" src={logoUrl} alt={logoAlt} />
          ) : (
            <div 
              className="h-8 w-8 rounded-lg flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: primary }}
            >
              {tenant?.name?.charAt(0)?.toUpperCase() || 'R'}
            </div>
          )}
          <div className="ml-3">
            <p className="text-sm font-medium" style={{ color: text }}>
              {tenant?.name || 'ReputationManager'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation - scrollable */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
        <nav className="mt-5 px-2 space-y-2 pb-4">
          <TooltipProvider>
            {navigation.map((item) => {
              const Icon = item.icon as React.ComponentType<any>;
              const linkContent = (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    item.current
                      ? 'bg-blue-100 border-r-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group flex items-center px-3 py-3 text-sm font-medium rounded-md`}
                >
                  <Icon className="mr-3 h-6 w-6" />
                  {item.name}
                </Link>
              );

              if (item.description) {
                return (
                  <Tooltip key={item.name}>
                    <TooltipTrigger asChild>
                      {linkContent}
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p className="max-w-xs">{item.description}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return linkContent;
            })}
          </TooltipProvider>
          
          {/* Server Admin Navigation (only for superusers) */}
          {serverAdminNavigation.length > 0 && (
            <>
              <div className="border-t border-gray-200 my-4"></div>
              <div className="px-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Server Admin
                </h3>
              </div>
              {serverAdminNavigation.map((item) => {
                const Icon = item.icon as React.ComponentType<any>;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                            className={`${
                              item.current
                                ? 'bg-purple-100 border-r-2 border-purple-600 text-purple-600'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            } group flex items-center px-3 py-3 text-sm font-medium rounded-md`}
                  >
                    <Icon className="mr-3 h-6 w-6" />
                    {item.name}
                  </Link>
                );
              })}
            </>
          )}
        </nav>
      </div>

      {/* Tenant Switcher - above user menu */}
      {tenants && tenants.length > 1 && (
        <div className="flex-shrink-0 border-t border-gray-200 p-4">
          <div className="relative">
            <button
              onClick={() => setShowTenantSwitcher(!showTenantSwitcher)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSwitching}
            >
              <div className="flex items-center flex-1 min-w-0">
                <span className="text-xs text-gray-500 mr-2">Workspace:</span>
                <span className="truncate">{tenant?.name || 'Select tenant'}</span>
              </div>
              <svg
                className={`ml-2 h-4 w-4 text-gray-400 transition-transform ${showTenantSwitcher ? 'transform rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showTenantSwitcher && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowTenantSwitcher(false)}
                />
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-300 rounded-md shadow-lg z-20 max-h-60 overflow-auto">
                  <div className="py-1">
                    {tenants.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => handleTenantSwitch(t.id)}
                        disabled={isSwitching}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50 ${
                          t.id === tenant?.id 
                            ? 'bg-blue-50 text-blue-700 font-medium' 
                            : 'text-gray-700'
                        } ${isSwitching ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="truncate">{t.name}</span>
                          {t.id === tenant?.id && (
                            <svg className="ml-2 h-4 w-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        {t.domain && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{t.domain}</p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* User menu - fixed at bottom */}
      <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
        <div className="flex-shrink-0 w-full group block">
          <div className="flex items-center">
            <div>
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-gray-300">
                <UserIcon />
              </div>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs font-medium text-gray-500 group-hover:text-gray-700">
                {user?.email}
              </p>
            </div>
            <button
              onClick={onLogout}
              className="ml-2 p-1 rounded-md text-gray-400 hover:text-gray-600"
              title="Logout"
            >
              <LogoutIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Icon components (replace with your preferred icon library)
const DashboardIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6a2 2 0 01-2 2H10a2 2 0 01-2-2V5z" />
  </svg>
);

const CampaignsIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
  </svg>
);

const FeedbackIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const AnalyticsIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const TemplatesIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const SequencesIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
  </svg>
);

const ContactsIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const RatingIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const BookOpenIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

const ServerSettingsIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
  </svg>
);

const ServerAdminIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
  </svg>
);

const BrandingIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
  </svg>
);
